import React from 'react';
import { Link } from 'react-router-dom';
import useSiteBanner from '../hooks/useSiteBanner';

const SITE_HOSTS = ['yournest.in', 'www.yournest.in'];
const LINK_CLASS = 'underline font-semibold hover:text-gray-300';

/** Only plain web links or site paths are allowed (never javascript:, data:, ...). */
export function isSafeBannerUrl(url) {
  return typeof url === 'string' && (/^https?:\/\//i.test(url) || (url.startsWith('/') && !url.startsWith('//')));
}

function BannerLink({ url, children }) {
  if (url.startsWith('/')) {
    return (
      <Link to={url} className={LINK_CLASS}>
        {children}
      </Link>
    );
  }

  let sameSite = false;
  try {
    sameSite = SITE_HOSTS.includes(new URL(url).hostname);
  } catch {
    sameSite = false;
  }
  return (
    <a
      href={url}
      className={LINK_CLASS}
      {...(sameSite ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {children}
    </a>
  );
}

/** Message with `link_text` hyperlinked (or the whole message when link_text is blank / not found). */
function BannerMessage({ message, linkText, linkUrl }) {
  if (!linkUrl || !isSafeBannerUrl(linkUrl)) return message;

  const start = linkText ? message.indexOf(linkText) : -1;
  if (start === -1) return <BannerLink url={linkUrl}>{message}</BannerLink>;

  return (
    <>
      {message.slice(0, start)}
      <BannerLink url={linkUrl}>{linkText}</BannerLink>
      {message.slice(start + linkText.length)}
    </>
  );
}

/** The black bar itself. Also used for the live preview in the admin. */
export function SiteBannerBar({ banner }) {
  if (!banner?.is_enabled || !banner.message?.trim()) return null;

  return (
    <div className="bg-black text-white" data-testid="site-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-2 flex items-center justify-center text-center text-xs sm:text-sm leading-snug">
          <p>
            <BannerMessage message={banner.message} linkText={banner.link_text} linkUrl={banner.link_url} />
          </p>
        </div>
      </div>
    </div>
  );
}

/** Black bar at the top of every public page, editable from Admin > Site Banner. */
export default function SiteBanner() {
  const banner = useSiteBanner();
  return <SiteBannerBar banner={banner} />;
}
